import { useState, useEffect } from 'react';
import { fetchCacheStats, clearCache } from '@/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Database, Trash2, RefreshCw } from 'lucide-react';

export default function CacheTools() {
  const [stats, setStats] = useState(null);
  const [pattern, setPattern] = useState('');
  const [message, setMessage] = useState('');

  const loadStats = () => {
    fetchCacheStats().then(res => setStats(res.data)).catch(() => {});
  };

  useEffect(() => { loadStats(); }, []);

  const handleClear = async (p) => {
    try {
      const res = await clearCache(p || undefined);
      setMessage(`Cleared ${res.data.keys_removed} keys`);
      setPattern('');
      loadStats();
      setTimeout(() => setMessage(''), 3000);
    } catch {
      setMessage('Failed to clear cache');
    }
  };

  return (
    <div data-testid="cache-tools-page">
      <h1 className="text-2xl font-bold tracking-tight uppercase text-slate-50 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
        <Database className="w-5 h-5 inline mr-2 text-blue-400" />
        Cache Tools
      </h1>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-blue-400 tabular-nums">{stats.total_keys}</p>
            <p className="text-xs text-slate-500 mt-1">Total Keys</p>
          </div>
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-emerald-400 tabular-nums">{stats.valid_keys}</p>
            <p className="text-xs text-slate-500 mt-1">Valid Keys</p>
          </div>
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-amber-400 tabular-nums">{stats.expired_keys}</p>
            <p className="text-xs text-slate-500 mt-1">Expired</p>
          </div>
        </div>
      )}

      {/* Clear Actions */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 space-y-4">
        <div className="flex gap-2">
          <Input
            value={pattern}
            onChange={e => setPattern(e.target.value)}
            placeholder="Pattern to clear (e.g., 'match', 'home')"
            className="bg-slate-800 border-slate-700 text-slate-200"
            data-testid="cache-pattern-input"
          />
          <Button
            onClick={() => handleClear(pattern)}
            className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/20 shrink-0"
            data-testid="clear-pattern-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear Pattern
          </Button>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={() => handleClear(null)}
            className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/20"
            data-testid="clear-all-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear All Cache
          </Button>
          <Button
            onClick={loadStats}
            variant="outline"
            className="border-slate-700 text-slate-400"
            data-testid="refresh-stats-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Stats
          </Button>
        </div>

        {message && (
          <p className="text-sm text-emerald-400" data-testid="cache-message">{message}</p>
        )}
      </div>
    </div>
  );
}
